import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    getAllProducts, getAllCategories, getVariantsByProduct,
    getImageUrl, getAllSubCategories, getAllBrands
} from '../api';

const toSlug = (name) =>
    name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';

const colors = {
    brown: '#3D2314',
    green: '#3A7D44',
    orange: '#E8720C',
    bgLight: '#FAF8F5',
    bgCard: '#FFFFFF',
    red: '#C0392B',
    muted: '#9B8EA0',
    border: '#EDE8E1',
    textPrimary: '#1C1C1C',
    textSecondary: '#7A7A7A',
};

const SIZE_UNITS = ['S', 'M', 'L', 'XL', 'XXL'];
const API_BASE_URL = process.env.REACT_APP_API_URL;
const PRODUCTS_PER_PAGE = 15;

const getCustomerId = () => {
    const customer = JSON.parse(localStorage.getItem('customer'));
    return customer?.id || null;
};

const getVariantLabel = (v) => {
    if (!v) return 'Default';
    const n = (v.variantName || '').trim(), u = (v.unit || '').trim();
    if (SIZE_UNITS.includes(u.toUpperCase())) return u.toUpperCase();
    return n && u ? `${n} ${u}` : n || u || 'Default';
};

const normalizeVariantId = (variantId) => {
    if (variantId === null || variantId === undefined) return 'default';
    if (variantId === 'default') return 'default';
    return String(variantId);
};

const makeCartId = (productId, variantId) =>
    `${productId}-${normalizeVariantId(variantId)}`;

const getCartedQty = (productId, variantId) => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const cartId = makeCartId(productId, variantId);
    const item = cart.find(i => i.cartId === cartId);
    return item ? item.quantity : 0;
};

// ─── Global CSS ───────────────────────────────
const GLOBAL_STYLES = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .pc-card {
        background: #fff;
        border: 1px solid #EDE8E1;
        border-radius: 16px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        transition: box-shadow 0.3s ease, transform 0.3s ease;
        font-family: 'DM Sans', sans-serif;
    }
    .pc-card:hover {
        box-shadow: 0 12px 40px rgba(0,0,0,0.10);
        transform: translateY(-4px);
    }

    .pc-img-wrap {
        position: relative;
        height: 220px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        cursor: pointer;
        border-bottom: 1px solid #EDE8E1;
    }
    .pc-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 18px;
        transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    .pc-card:hover .pc-img { transform: scale(1.06); }

    .pc-discount-badge {
        position: absolute;
        top: 12px;
        left: 12px;
        background: #C0392B;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 20px;
        letter-spacing: 0.04em;
        font-family: 'DM Sans', sans-serif;
        z-index: 2;
    }

    .pc-oos-badge {
        position: absolute;
        top: 12px;
        left: 12px;
        background: #B91C1C;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 20px;
        z-index: 2;
        font-family: 'DM Sans', sans-serif;
    }

    .pc-low-stock-badge {
        position: absolute;
        bottom: 10px;
        left: 12px;
        background: #FFF7ED;
        border: 1px solid #FED7AA;
        color: #C2410C;
        font-size: 9px;
        font-weight: 700;
        padding: 2px 8px;
        border-radius: 20px;
        z-index: 2;
        font-family: 'DM Sans', sans-serif;
    }

    .pc-action-bar {
        position: absolute;
        bottom: 0; left: 0; right: 0;
        background: rgba(255,255,255,0.97);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 14px;
        border-top: 1px solid #EDE8E1;
        transform: translateY(100%);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: 3;
    }
    .pc-card:hover .pc-action-bar { transform: translateY(0); }

    .pc-mobile-footer { display: none; }

    .pc-mobile-wish-btn {
        background: #FAF8F5;
        border: 1px solid #EDE8E1;
        border-radius: 10px;
        width: 38px; height: 38px;
        font-size: 16px;
        display: flex; align-items: center; justify-content: center;
    }
    .pc-mobile-cart-btn {
        flex: 1;
        background: #0C3D7A;
        color: #fff;
        border: none;
        border-radius: 10px;
        padding: 10px;
        font-size: 12px;
        font-weight: 700;
        font-family: 'DM Sans', sans-serif;
        cursor: pointer;
    }
    .pc-mobile-qv-btn {
        background: #FAF8F5;
        border: 1px solid #EDE8E1;
        border-radius: 10px;
        width: 38px; height: 38px;
        font-size: 15px;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer;
    }

    .pc-icon-btn {
        background: none; border: none; cursor: pointer; font-size: 17px;
        padding: 5px; border-radius: 8px; transition: background 0.15s;
        line-height: 1; display: flex; align-items: center; justify-content: center;
    }
    .pc-icon-btn:hover { background: #F7F4F0; }

    .pc-cart-btn {
        background: #0C3D7A; color: #fff; border: none; border-radius: 8px;
        padding: 8px 18px; font-size: 11px; font-weight: 700;
        font-family: 'DM Sans', sans-serif; cursor: pointer;
        letter-spacing: 0.05em; text-transform: uppercase; transition: background 0.2s;
    }
    .pc-cart-btn:hover { background: #E8720C; }
    .pc-cart-btn:disabled {
        background: #9CA3AF !important;
        cursor: not-allowed !important;
        opacity: 0.7;
    }

    .pc-body {
        padding: 14px 16px 18px;
        display: flex; flex-direction: column; gap: 6px; flex: 1;
        font-family: 'DM Sans', sans-serif;
    }
    .pc-category-label {
        font-size: 10px; font-weight: 600; color: #B0A090;
        text-transform: uppercase; letter-spacing: 0.08em;
    }
    .pc-name {
        font-size: 14px; font-weight: 600; color: #1C1C1C;
        line-height: 1.4; height: 40px; overflow: hidden; cursor: pointer;
        margin: 0; letter-spacing: -0.01em;
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    }
    .pc-name:hover { color: #E8720C; }
    .pc-brand { font-size: 11px; font-weight: 600; color: #3A7D44; }
    .pc-stars { color: #F5B301; font-size: 11px; letter-spacing: 1px; }

    .pc-variants { display: flex; flex-wrap: wrap; gap: 5px; margin: 2px 0; }
    .pc-variant-btn {
        padding: 3px 9px; font-size: 10px; font-weight: 600; border-radius: 5px;
        cursor: pointer; border: 1.5px solid #EDE8E1; background: #fff; color: #888;
        transition: all 0.15s; font-family: 'DM Sans', sans-serif;
    }
    .pc-variant-btn.active { background: #F57C00; color: #fff; }
    .pc-variant-btn:hover:not(.active) { border-color: #C0A898; color: #3D2314; }
    .pc-variant-btn.oos {
        opacity: 0.4;
        text-decoration: line-through;
        cursor: not-allowed;
    }

    .pc-price-row { display: flex; align-items: baseline; gap: 8px; margin-top: 4px; }
    .pc-price {
        font-size: 20px; font-weight: 700; color: #1C1C1C;
        letter-spacing: -0.03em; font-family: 'DM Sans', sans-serif;
    }
    .pc-old-price {
        font-size: 12px; text-decoration: line-through; color: #C4BAB2;
        font-family: 'DM Sans', sans-serif;
    }
    .pc-save-tag {
        font-size: 10px; font-weight: 700; color: #2D6A4F; background: #D8F3DC;
        padding: 2px 8px; border-radius: 4px; display: inline-block;
        width: fit-content; letter-spacing: 0.03em; font-family: 'DM Sans', sans-serif;
    }

    .sb-filter-btn {
        display: block; width: 100%; text-align: left; padding: 7px 10px;
        font-size: 13px; font-weight: 500; border-radius: 8px; border: none;
        cursor: pointer; margin-bottom: 2px; transition: all 0.15s;
        font-family: 'DM Sans', sans-serif; background: none; color: #555;
    }
    .sb-filter-btn:hover { background: #FAF8F5; color: #3D2314; }
    .sb-filter-btn.active { background: #FEF0E5; color: #E8720C; font-weight: 700; }

    /* ── Brand & SubCategory image buttons ── */
    .sb-img-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        text-align: left;
        padding: 5px 8px;
        font-size: 13px;
        font-weight: 500;
        border-radius: 8px;
        border: none;
        cursor: pointer;
        margin-bottom: 3px;
        transition: all 0.15s;
        font-family: 'DM Sans', sans-serif;
        background: none;
        color: #555;
    }
    .sb-img-btn:hover { background: #FAF8F5; color: #3D2314; }
    .sb-img-btn.active { background: #FEF0E5; color: #E8720C; font-weight: 700; }
    .sb-img-thumb {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        object-fit: cover;
        border: 1.5px solid #EDE8E1;
        background: #F9F6F2;
        flex-shrink: 0;
        transition: border-color 0.15s;
    }
    .sb-img-btn.active .sb-img-thumb { border-color: #E8720C; }
    .sb-img-placeholder {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: #F0EDE8;
        border: 1.5px solid #EDE8E1;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        color: #B0A090;
        flex-shrink: 0;
        text-transform: uppercase;
    }

    .pf-range-slider-wrap {
        position: relative;
        height: 20px;
        margin: 6px 4px 18px;
    }
    .pf-range-track {
        position: absolute;
        top: 8px; left: 0; right: 0;
        height: 4px;
        background: #EDE8E1;
        border-radius: 2px;
    }
    .pf-range-fill {
        position: absolute;
        top: 8px;
        height: 4px;
        background: #E8720C;
        border-radius: 2px;
    }
    input[type=range].pf-slider {
        position: absolute;
        top: 0;
        width: 100%;
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
        pointer-events: none;
        height: 20px;
        margin: 0;
    }
    input[type=range].pf-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px; height: 18px;
        border-radius: 50%;
        background: #fff;
        border: 2.5px solid #E8720C;
        cursor: pointer;
        pointer-events: all;
        transition: box-shadow 0.15s;
        box-shadow: 0 1px 4px rgba(232,114,12,0.25);
    }
    input[type=range].pf-slider::-webkit-slider-thumb:hover {
        box-shadow: 0 0 0 6px rgba(232,114,12,0.12);
    }
    input[type=range].pf-slider::-moz-range-thumb {
        width: 18px; height: 18px;
        border-radius: 50%;
        background: #fff;
        border: 2.5px solid #E8720C;
        cursor: pointer;
        pointer-events: all;
    }

    /* ── Price filter input row — FIXED for mobile ── */
    .pf-filter-wrap {
        width: 100%;
        box-sizing: border-box;
    }
    .pf-input-row {
        display: flex;
        flex-direction: row;
        gap: 5px;
        align-items: stretch;
        width: 100%;
        box-sizing: border-box;
    }
    .pf-input-box {
        flex: 1 1 0;
        min-width: 0;
        width: 0;
        border: 1px solid #EDE8E1;
        border-radius: 7px;
        padding: 6px 6px;
        display: flex;
        align-items: center;
        gap: 2px;
        background: #fff;
        transition: border-color 0.15s;
        overflow: hidden;
        box-sizing: border-box;
    }
    .pf-input-box:focus-within { border-color: #E8720C; }
    .pf-input-box .pf-currency {
        font-size: 10px;
        color: #aaa;
        font-weight: 600;
        flex-shrink: 0;
        line-height: 1;
    }
    .pf-input-box input {
        border: none;
        width: 100%;
        min-width: 0;
        max-width: 100%;
        font-size: 11px;
        font-weight: 600;
        color: #1C1C1C;
        outline: none;
        background: transparent;
        font-family: 'DM Sans', sans-serif;
        padding: 0;
    }
    .pf-sep {
        color: #ccc;
        font-size: 12px;
        flex-shrink: 0;
        line-height: 1;
        align-self: center;
    }
    .pf-go-btn {
        flex: 0 0 auto;
        flex-shrink: 0;
        background: #E8720C;
        color: #fff;
        border: none;
        border-radius: 7px;
        padding: 6px 10px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        font-family: 'DM Sans', sans-serif;
        transition: background 0.2s;
        white-space: nowrap;
        line-height: 1;
        align-self: stretch;
        display: flex;
        align-items: center;
    }
    .pf-go-btn:hover { background: #C0620A; }

    .price-dropdown-wrap {
        position: relative;
        z-index: 600;
    }
    /* Hide top-bar price dropdown on mobile — price is inside drawer */
    @media (max-width: 1024px) { .price-dropdown-wrap { display: none; } }
    .price-dropdown-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px;
        background: #fff;
        border: 1.5px solid #EDE8E1;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 600;
        color: #555;
        cursor: pointer;
        font-family: 'DM Sans', sans-serif;
        transition: border-color 0.2s, color 0.2s;
        white-space: nowrap;
    }
    .price-dropdown-btn:hover,
    .price-dropdown-btn.active { border-color: #E8720C; color: #E8720C; }
    .price-dropdown-btn .pd-arrow {
        font-size: 10px;
        transition: transform 0.2s;
        color: #bbb;
    }
    .price-dropdown-btn.active .pd-arrow { transform: rotate(180deg); color: #E8720C; }
    .price-dropdown-panel {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        width: 280px;
        background: #fff;
        border: 1.5px solid #EDE8E1;
        border-radius: 14px;
        padding: 16px;
        box-shadow: 0 12px 40px rgba(0,0,0,0.10);
        z-index: 601;
        animation: dropdownFadeIn 0.18s ease;
    }
    @keyframes dropdownFadeIn {
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: translateY(0); }
    }
    .price-filtered-dot {
        width: 7px; height: 7px;
        border-radius: 50%;
        background: #E8720C;
        display: inline-block;
        flex-shrink: 0;
    }

    @keyframes slideInRight { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
    .rc-toast {
        position: fixed; bottom: 28px; right: 28px; z-index: 99999;
        display: flex; align-items: center; gap: 14px;
        min-width: 300px; padding: 14px 18px; border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.12);
        animation: slideInRight 0.35s cubic-bezier(.4,0,.2,1) forwards;
        font-family: 'DM Sans', sans-serif;
    }
    .rc-toast-success { background: linear-gradient(135deg, #1B5E20, #2E7D32); color: #fff; }
    .rc-toast-info    { background: linear-gradient(135deg, #0D47A1, #1565C0); color: #fff; }
    .rc-toast-warn    { background: linear-gradient(135deg, #92400E, #B45309); color: #fff; }
    .rc-toast-body    { display: flex; flex-direction: column; gap: 2px; }
    .rc-toast-progress {
        position: absolute; bottom: 0; left: 0; height: 3px;
        background: rgba(255,255,255,0.4);
        animation: toastProgress 3s linear forwards; width: 100%;
        border-radius: 0 0 12px 12px;
    }
    @keyframes toastProgress { from { width: 100%; } to { width: 0%; } }

    .pg-btn {
        padding: 8px 14px; border-radius: 8px; font-size: 13px;
        border: 1px solid #EDE8E1; background: #fff; color: #555;
        cursor: pointer; transition: all 0.15s; min-width: 38px;
        text-align: center; font-family: 'DM Sans, sans-serif'; font-weight: 500;
    }
    .pg-btn:hover:not(:disabled) { border-color: #E8720C; color: #E8720C; }
    .pg-btn.active { border-color: #E8720C; background: #FEF0E5; color: #E8720C; font-weight: 700; }
    .pg-btn:disabled { background: #f5f5f5; color: #ccc; cursor: not-allowed; }

    .sb-overlay {
        display: none;
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.5);
        z-index: 998;
        backdrop-filter: blur(2px);
    }
    .sb-overlay.open { display: block; }

    .sb-drawer {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        width: 100%; height: 100%;
        background: #fff;
        z-index: 999;
        overflow: hidden;
        display: flex; flex-direction: column;
        transform: translateY(100%);
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 -8px 40px rgba(0,0,0,0.18);
    }
    .sb-drawer.open { transform: translateY(0); }

    .sb-modal-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px 20px; border-bottom: 1px solid #EDE8E1;
        background: #fff; flex-shrink: 0;
    }
    .sb-modal-title {
        font-size: 17px; font-weight: 700; color: #1C1C1C;
        font-family: 'DM Sans', sans-serif; letter-spacing: -0.01em;
    }
    .sb-drawer-close {
        background: #F5F3F0; border: none; border-radius: 50%;
        width: 34px; height: 34px; font-size: 15px; cursor: pointer; color: #555;
        display: flex; align-items: center; justify-content: center;
        transition: background 0.15s; flex-shrink: 0;
    }
    .sb-drawer-close:hover { background: #EDE8E1; }

    .sb-modal-body {
        flex: 1; overflow-y: auto; overflow-x: hidden; padding: 16px 20px 20px;
        -webkit-overflow-scrolling: touch;
        box-sizing: border-box;
    }

    .sb-modal-footer {
        display: flex; gap: 10px; padding: 14px 20px;
        border-top: 1px solid #EDE8E1; background: #fff; flex-shrink: 0;
    }
    .sb-apply-btn {
        flex: 1; background: #0C3D7A; color: #fff; border: none;
        border-radius: 10px; padding: 13px; font-size: 14px; font-weight: 700;
        font-family: 'DM Sans', sans-serif; cursor: pointer;
        letter-spacing: 0.02em; transition: background 0.2s;
    }
    .sb-apply-btn:hover { background: #E8720C; }
    .sb-clear-btn {
        background: #FEF0E5; color: #E8720C; border: 1.5px solid #F5CBA7;
        border-radius: 10px; padding: 13px 18px; font-size: 14px; font-weight: 700;
        font-family: 'DM Sans', sans-serif; cursor: pointer;
        transition: background 0.15s; white-space: nowrap;
    }
    .sb-clear-btn:hover { background: #fde8d0; }

    .filter-toggle-btn {
        display: none; align-items: center; gap: 6px;
        padding: 8px 16px; background: #FEF0E5;
        border: 1.5px solid #F5CBA7; border-radius: 10px;
        color: #E8720C; font-size: 13px; font-weight: 700;
        font-family: 'DM Sans', sans-serif; cursor: pointer; letter-spacing: 0.03em;
    }

    .shop-layout {
        display: grid; grid-template-columns: 220px 1fr;
        gap: 2.5rem; align-items: start;
    }
    .shop-sidebar-static {
        background: #fff; border: 1px solid #EDE8E1;
        border-radius: 14px; padding: 1.25rem;
        position: sticky; top: 110px;
    }
    .shop-main { min-width: 0; }
    .shop-products-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 1.5rem;
    }

    @media (min-width: 768px) and (max-width: 1024px) {
        .shop-layout { grid-template-columns: 1fr; }
        .shop-sidebar-static { display: none; }
        .filter-toggle-btn { display: flex; }
        .shop-products-grid { grid-template-columns: repeat(3, 1fr); gap: 1rem; }
        .pc-img-wrap { height: 180px; }
        .pc-action-bar { display: none !important; }
        .pc-mobile-footer {
            display: flex; align-items: center; gap: 8px;
            padding: 10px; border-top: 1px solid #EDE8E1; background: #fff;
        }
        .pc-cart-btn { padding: 7px 12px; font-size: 10px; }
        .rc-toast { bottom: 16px; right: 50%; transform: translateX(50%); min-width: 260px; }
        .pg-btn { padding: 7px 10px; font-size: 12px; min-width: 34px; }
        .price-dropdown-panel { width: 260px; }
    }

    @media (min-width: 600px) and (max-width: 767px) {
        .shop-layout { grid-template-columns: 1fr; }
        .shop-sidebar-static { display: none; }
        .filter-toggle-btn { display: flex; }
        .shop-products-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
        .pc-img-wrap { height: 160px; }
        .pc-action-bar { display: none !important; }
        .pc-mobile-footer {
            display: flex; align-items: center; gap: 8px;
            padding: 10px; border-top: 1px solid #EDE8E1; background: #fff;
        }
        .pc-cart-btn { padding: 6px 10px; font-size: 10px; }
        .pc-price { font-size: 17px; }
        .pc-name { font-size: 13px; }
        .rc-toast { bottom: 12px; right: 50%; transform: translateX(50%); min-width: 240px; }
        .pg-btn { padding: 6px 9px; font-size: 11px; min-width: 30px; }
        .price-dropdown-panel { width: 240px; }
    }

    @media (max-width: 599px) {
        .shop-layout { grid-template-columns: 1fr; gap: 0; }
        .shop-sidebar-static { display: none; }
        .filter-toggle-btn { display: flex; width: 100%; justify-content: center; margin-bottom: 12px; }
        .shop-products-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
        .pc-img-wrap { height: 140px; }
        .pc-img { padding: 10px; }
        .pc-action-bar { display: none !important; }
        .pc-mobile-footer {
            display: flex; align-items: center; gap: 6px;
            padding: 8px 10px 10px; border-top: 1px solid #EDE8E1; background: #fff;
        }
        .pc-mobile-wish-btn { width: 34px; height: 34px; font-size: 15px; border-radius: 8px; cursor: pointer; }
        .pc-mobile-cart-btn { font-size: 11px; padding: 9px 6px; border-radius: 8px; }
        .pc-mobile-qv-btn { width: 34px; height: 34px; font-size: 14px; border-radius: 8px; }
        .pc-body { padding: 8px 10px 10px; gap: 3px; }
        .pc-name { font-size: 12px; height: 34px; }
        .pc-price { font-size: 15px; }
        .pc-old-price { font-size: 11px; }
        .pc-save-tag { font-size: 9px; padding: 1px 6px; }
        .pc-category-label { font-size: 9px; }
        .pc-brand { font-size: 10px; }
        .pc-stars { font-size: 10px; }
        .pc-variant-btn { padding: 2px 6px; font-size: 9px; }
        .rc-toast { bottom: 0; right: 0; left: 0; min-width: unset; width: 100%; border-radius: 12px 12px 0 0; transform: none; }
        .pg-btn { padding: 6px 8px; font-size: 11px; min-width: 28px; }

    }

    /* CategoryStrip non-sticky on mobile to prevent stacking context trapping drawer */
    @media (max-width: 1024px) {
        .sl-wrapper-outer { position: relative !important; top: auto !important; z-index: 1 !important; }
    }

    @media (max-width: 380px) {
        .shop-products-grid { grid-template-columns: 1fr; gap: 12px; }
        .pc-img-wrap { height: 200px; }
        .pc-img { padding: 16px; }
        .pc-name { font-size: 13px; height: auto; }
        .pc-price { font-size: 18px; }
        .pc-mobile-cart-btn { font-size: 12px; padding: 10px 8px; }
    }
`;

// ─── Pagination ───────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
        else {
            if (currentPage <= 4) pages.push(1, 2, 3, 4, 5, '...', totalPages);
            else if (currentPage >= totalPages - 3) pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            else pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
        }
        return pages;
    };
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '2.5rem', flexWrap: 'wrap' }}>
            <button className="pg-btn" onClick={() => { onPageChange(currentPage - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={currentPage === 1}>← Prev</button>
            {getPageNumbers().map((page, idx) =>
                page === '...'
                    ? <span key={`e-${idx}`} style={{ padding: '8px 4px', color: '#bbb', fontSize: '13px' }}>…</span>
                    : <button key={page} className={`pg-btn${currentPage === page ? ' active' : ''}`}
                        onClick={() => { onPageChange(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>{page}</button>
            )}
            <button className="pg-btn" onClick={() => { onPageChange(currentPage + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={currentPage === totalPages}>Next →</button>
            <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '8px', fontFamily: 'DM Sans, sans-serif' }}>Page {currentPage} of {totalPages}</span>
        </div>
    );
};

// ─── ImageWithFallback ────────────────────────
const ImageWithFallback = ({ src, alt }) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => { setFailed(false); }, [src]);
    if (failed) return <span style={{ fontSize: '32px' }}>🛍️</span>;
    return <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={() => setFailed(true)} />;
};

// ─── SidebarThumb helper ──────────────────────
const SidebarThumb = ({ src, alt }) => {
    const [failed, setFailed] = useState(false);
    if (!src || failed) {
        return (
            <span className="sb-img-placeholder">
                {(alt || '?').charAt(0)}
            </span>
        );
    }
    return (
        <img
            src={src}
            alt={alt}
            className="sb-img-thumb"
            onError={() => setFailed(true)}
        />
    );
};

const CategoryStrip = ({ categories, activeCategory, onSelect }) => {
    const MIN_ITEMS = 20;
    const repeatCount = categories.length > 0
        ? Math.ceil(MIN_ITEMS / categories.length) * 2
        : 2;
    const items = Array.from({ length: repeatCount }, () => categories).flat();
    const halfLength = items.length / 2;
    const loopItems = items.slice(0, halfLength);
    const finalItems = [...loopItems, ...loopItems];

    const hasSelection = activeCategory && activeCategory !== 'All';

    return (
        <div className="sl-wrapper-outer" style={{
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            padding: '14px 0',
            marginTop: '50px',
            marginBottom: '50px',
        }}>
            <style>{`
                @keyframes seamless-loop {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .sl-track {
                    display: flex;
                    width: max-content;
                    animation: seamless-loop 30s linear infinite;
                    will-change: transform;
                }
                .sl-track:hover { animation-play-state: paused; }
                .sl-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    flex-shrink: 0;
                    cursor: pointer;
                    padding: 0 28px;
                }
                .sl-circle {
                    width: 70px;
                    height: 70px;
                    border-radius: 50%;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid #f0e8df;
                    background: #FEF9F3;
                    flex-shrink: 0;
                    transition: border-color 0.25s, box-shadow 0.25s, filter 0.25s, opacity 0.25s;
                }
                .sl-track:hover .sl-circle { filter: grayscale(100%); opacity: 0.45; }
                .sl-track:hover .sl-item:hover .sl-circle { filter: grayscale(0%); opacity: 1; border-color: #C0A898; }
                .sl-track.has-selection .sl-circle { filter: grayscale(0%); opacity: 1; }
                .sl-track.has-selection:hover .sl-circle { filter: grayscale(0%); opacity: 0.5; }
                .sl-track.has-selection:hover .sl-item:hover .sl-circle { opacity: 1; border-color: #C0A898; }
                .sl-circle.active { filter: grayscale(0%) !important; opacity: 1 !important; border-color: #F58220 !important; box-shadow: 0 0 10px rgba(245,130,32,0.2); }
                .sl-name { font-size: 13px; color: #333; text-align: center; font-family: 'DM Sans', sans-serif; font-weight: 500; white-space: nowrap; transition: color 0.2s, opacity 0.2s; }
                .sl-track:hover .sl-name { color: #bbb; opacity: 0.6; }
                .sl-track:hover .sl-item:hover .sl-name { color: #E8720C; opacity: 1; }
                .sl-track.has-selection .sl-name { color: #333; opacity: 1; }
                .sl-track.has-selection:hover .sl-name { color: #bbb; opacity: 0.5; }
                .sl-track.has-selection:hover .sl-item:hover .sl-name { color: #E8720C; opacity: 1; }
                .sl-circle.active ~ .sl-name { color: #E8720C !important; opacity: 1 !important; font-weight: 600; }
                .sl-wrapper { position: relative; overflow: hidden; }
                .sl-wrapper::before, .sl-wrapper::after { content: ''; position: absolute; top: 0; bottom: 0; width: 100px; z-index: 2; pointer-events: none; }
                .sl-wrapper::before { left: 0; background: linear-gradient(to right, #ffffff 0%, transparent 100%); }
                .sl-wrapper::after { right: 0; background: linear-gradient(to left, #ffffff 0%, transparent 100%); }
            `}</style>

            <div className="sl-wrapper">
                <div className={`sl-track${hasSelection ? ' has-selection' : ''}`}>
                    {finalItems.map((cat, index) => {
                        const isActive = cat.name === activeCategory;
                        const imgSrc = cat.image ? getImageUrl(cat.image) : null;
                        return (
                            <div key={`${cat.id}-${index}`} className="sl-item" onClick={() => onSelect(cat.name)}>
                                <div className={`sl-circle${isActive ? ' active' : ''}`}>
                                    {imgSrc
                                        ? <ImageWithFallback src={imgSrc} alt={cat.name} />
                                        : <span style={{ fontSize: '28px' }}>🛍️</span>
                                    }
                                </div>
                                <span className="sl-name">{cat.name}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ─── PriceFilter ──────────────────────────────
const PriceFilter = ({ priceRange, setPriceRange, maxPrice, onClose }) => {
    const PRESETS = [].filter(p => p.max <= maxPrice || p.min < maxPrice);
    const [activePreset, setActivePreset] = useState(null);
    const [localMin, setLocalMin] = useState(priceRange[0]);
    const [localMax, setLocalMax] = useState(priceRange[1]);

    useEffect(() => {
        setLocalMin(priceRange[0]);
        setLocalMax(priceRange[1]);
    }, [priceRange]);

    const safeMax = maxPrice > 0 ? maxPrice : 10000;
    const pct1 = (priceRange[0] / safeMax) * 100;
    const pct2 = (priceRange[1] / safeMax) * 100;

    const handlePreset = (i) => {
        if (activePreset === i) {
            setActivePreset(null);
            setPriceRange([0, safeMax]);
            setLocalMin(0);
            setLocalMax(safeMax);
        } else {
            setActivePreset(i);
            const p = PRESETS[i];
            const clamped = Math.min(p.max, safeMax);
            setPriceRange([p.min, clamped]);
            setLocalMin(p.min);
            setLocalMax(clamped);
        }
        if (onClose) onClose();
    };

    const handleMinSlider = (e) => {
        const v = Math.min(parseInt(e.target.value), priceRange[1] - 1);
        setPriceRange([v, priceRange[1]]);
        setLocalMin(v);
        setActivePreset(null);
    };

    const handleMaxSlider = (e) => {
        const v = Math.max(parseInt(e.target.value), priceRange[0] + 1);
        setPriceRange([priceRange[0], v]);
        setLocalMax(v);
        setActivePreset(null);
    };

    const applyCustom = () => {
        const mn = Math.max(0, Math.min(parseInt(localMin) || 0, safeMax));
        const mx = Math.max(mn + 1, Math.min(parseInt(localMax) || safeMax, safeMax));
        setPriceRange([mn, mx]);
        setLocalMin(mn);
        setLocalMax(mx);
        setActivePreset(null);
    };

    return (
        <div className="pf-filter-wrap" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {PRESETS.map((p, i) => (
                <button key={i} className={`sb-filter-btn${activePreset === i ? ' active' : ''}`} onClick={() => handlePreset(i)}>
                    {p.label}
                </button>
            ))}
            {PRESETS.length > 0 && <div style={{ height: '1px', background: '#EDE8E1', margin: '8px 0 12px' }} />}
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px', fontWeight: 500 }}>
                ₹{priceRange[0].toLocaleString('en-IN')}
                <span style={{ margin: '0 4px', color: '#ccc' }}>–</span>
                ₹{priceRange[1].toLocaleString('en-IN')}
            </div>
            <div className="pf-range-slider-wrap">
                <div className="pf-range-track" />
                <div className="pf-range-fill" style={{ left: `${pct1}%`, width: `${pct2 - pct1}%` }} />
                <input type="range" className="pf-slider" min={0} max={safeMax} step={1} value={priceRange[0]} onChange={handleMinSlider} />
                <input type="range" className="pf-slider" min={0} max={safeMax} step={1} value={priceRange[1]} onChange={handleMaxSlider} />
            </div>

            {/* ── FIXED: responsive input row ── */}
            <div className="pf-input-row">
                <div className="pf-input-box">
                    <span className="pf-currency">₹</span>
                    <input
                        value={localMin}
                        onChange={(e) => setLocalMin(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyCustom()}
                        placeholder="Min"
                    />
                </div>
                <span className="pf-sep">—</span>
                <div className="pf-input-box">
                    <span className="pf-currency">₹</span>
                    <input
                        value={localMax}
                        onChange={(e) => setLocalMax(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyCustom()}
                        placeholder="Max"
                    />
                </div>
                <button className="pf-go-btn" onClick={applyCustom}>Go</button>
            </div>
        </div>
    );
};

// ─── PriceDropdown ────────────────────────────
const PriceDropdown = ({ priceRange, setPriceRange, maxPrice }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const isPriceFiltered = priceRange[0] !== 0 || priceRange[1] !== maxPrice;

    useEffect(() => {
        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

   
};

// ─── ProductCard ──────────────────────────────
const ProductCard = ({ product, onShowToast }) => {
    const navigate = useNavigate();
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);

    const [cartVersion, setCartVersion] = useState(0);
    useEffect(() => {
        const onStorage = () => setCartVersion(v => v + 1);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        if (product.variants?.length > 0) setSelectedVariant(product.variants[0]);
        else setSelectedVariant({
            id: 'default', variantName: 'Default', unit: '',
            mrp: parseFloat(product.mrp) || 0,
            salesPrice: parseFloat(product.salesPrice) || 0,
            stock: parseInt(product.stock ?? 999),
        });
    }, [product]);

    useEffect(() => {
        const wl = JSON.parse(localStorage.getItem('wishlist')) || [];
        setIsWishlisted(wl.some(i => i.id === product.id));
    }, [product.id]);

    const cartedQty = useMemo(
        () => selectedVariant ? getCartedQty(product.id, selectedVariant.id) : 0,
        [product.id, selectedVariant, cartVersion]
    );

    if (!selectedVariant) return null;

    const imgSrc       = product.image ? getImageUrl(product.image) : '';
    const mrp          = parseFloat(selectedVariant.mrp) || 0;
    const salesPrice   = parseFloat(selectedVariant.salesPrice) || 0;
    const variantLabel = getVariantLabel(selectedVariant);
    const discountPct  = mrp > salesPrice ? Math.round(((mrp - salesPrice) / mrp) * 100) : 0;

    const actualStock    = parseInt(selectedVariant.stock ?? 999);
    const remainingStock = Math.max(0, actualStock - cartedQty);
    const isOutOfStock   = actualStock === 0;
    const isFullyCarted  = !isOutOfStock && remainingStock === 0;
    const isLowStock     = actualStock > 0 && actualStock <= 5;

    const handleProductClick = () => {
        const slug = toSlug(product.productName);
        navigate(`/product/${slug}`, {
            state: {
                product: {
                    ...product, name: product.productName,
                    price: salesPrice, oldPrice: mrp, img: imgSrc,
                    salesPrice, mrp, category: product.Category?.name || 'Traditional',
                    stock: actualStock,
                }
            }
        });
    };

    const handleQuickView = (e) => {
        e.stopPropagation();
        Swal.fire({
            html: `
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@400;500;600&display=swap');
                    .qv-wrap { font-family: 'DM Sans', sans-serif; text-align: left; padding: 0; }
                    .qv-top { display: flex; gap: 20px; align-items: flex-start; margin-bottom: 18px; }
                    .qv-img-box { position: relative; width: 140px; min-width: 140px; height: 150px; border-radius: 14px; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 1px solid #EDE8E1; flex-shrink: 0; }
                    .qv-img-box img { width: 90%; height: 90%; object-fit: contain; }
                    .qv-badge { position: absolute; top: 8px; left: 8px; background: #C0392B; color: #fff; font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 20px; }
                    .qv-info { flex: 1; display: flex; flex-direction: column; gap: 5px; padding-top: 2px; }
                    .qv-cat { font-size: 9px; font-weight: 700; color: #B0A090; text-transform: uppercase; letter-spacing: 0.1em; }
                    .qv-name { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 700; color: #1C1C1C; line-height: 1.25; margin: 0; }
                    .qv-brand { font-size: 11px; font-weight: 600; color: #3A7D44; }
                    .qv-stars { color: #F5B301; font-size: 12px; letter-spacing: 2px; margin-top: 2px; }
                    .qv-variant-label { font-size: 10px; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.08em; margin-top: 4px; }
                    .qv-variant-pill { display: inline-block; padding: 4px 12px; background: #FEF0E5; border: 1.5px solid #E8720C; font-size: 11px; font-weight: 700; color: #E8720C; }
                    .qv-price-section { background: linear-gradient(135deg, #FDF6EE 0%, #FEF9F3 100%); border: 1px solid #EDE8E1; border-radius: 14px; padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
                    .qv-price-left { display: flex; align-items: baseline; gap: 8px; }
                    .qv-price { font-family: 'Cormorant Garamond', serif; font-size: 34px; font-weight: 700; color: #1C1C1C; letter-spacing: -0.02em; line-height: 1; }
                    .qv-currency { font-family: 'DM Sans', sans-serif; font-size: 16px; font-weight: 600; color: #555; margin-bottom: 4px; }
                    .qv-old-price { font-size: 13px; text-decoration: line-through; color: #C4BAB2; }
                    .qv-save-badge { background: linear-gradient(135deg, #1B5E20, #2E7D32); color: #fff; font-size: 10px; font-weight: 700; padding: 4px 10px; border-radius: 20px; letter-spacing: 0.04em; white-space: nowrap; align-self: center; }
                    .qv-oos { background: #FEF2F2; border: 1px solid #FECACA; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 700; color: #B91C1C; text-align: center; margin-top: 10px; }
                    .qv-stock-info { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #15803D; text-align: center; margin-top: 10px; }
                </style>
                <div class="qv-wrap">
                    <div class="qv-top">
                        <div class="qv-img-box">
                            <img src="${imgSrc}" alt="${product.productName}" />
                            ${isOutOfStock ? '<span class="qv-badge" style="background:#B91C1C">Out of Stock</span>' : discountPct > 0 ? `<span class="qv-badge">${discountPct}% OFF</span>` : ''}
                        </div>
                        <div class="qv-info">
                            <span class="qv-cat">${product.Category?.name || 'Product'}</span>
                            <h2 class="qv-name">${product.productName}</h2>
                            ${product.Brand?.brandName ? `<span class="qv-brand">${product.Brand.brandName}</span>` : ''}
                            <div class="qv-stars">★★★★★</div>
                            <span class="qv-variant-label">Variant</span>
                            <span class="qv-variant-pill">${variantLabel}</span>
                        </div>
                    </div>
                    <div class="qv-price-section">
                        <div class="qv-price-left">
                            <span class="qv-currency">₹</span>
                            <span class="qv-price">${salesPrice}</span>
                            ${mrp > salesPrice ? `<span class="qv-old-price">₹${mrp}</span>` : ''}
                        </div>
                        ${mrp > salesPrice ? `<span class="qv-save-badge">SAVE ₹${(mrp - salesPrice).toFixed(0)} · ${discountPct}% OFF</span>` : ''}
                    </div>
                    ${isOutOfStock
                        ? '<div class="qv-oos">⚠️ This item is currently out of stock</div>'
                        : `<div class="qv-stock-info">✅ ${remainingStock} remaining · ${cartedQty} already in your cart</div>`
                    }
                </div>
            `,
            showConfirmButton: true,
            confirmButtonText: 'Close',
            background: '#fff',
            width: '420px',
            padding: '1.5rem',
            borderRadius: '20px',
        });
    };

const handleAdd = async (e) => {
    e.stopPropagation();
 
    // ── Guest check ──────────────────────────────────────────
    const customerId = getCustomerId();
    if (!customerId) {
        onShowToast('Please login to add items to your cart!', 'warn');
        return;
    }
 
    if (isOutOfStock) { onShowToast(`"${product.productName}" is out of stock!`, 'warn'); return; }
    const cart   = JSON.parse(localStorage.getItem('cart')) || [];
    const cartId = makeCartId(product.id, selectedVariant.id);
    const idx    = cart.findIndex(i => i.cartId === cartId);
    const currentCartedQty = idx !== -1 ? cart[idx].quantity : 0;
    if (currentCartedQty >= actualStock) { onShowToast(`Only ${actualStock} in stock — you already have ${currentCartedQty} in your cart!`, 'warn'); return; }
    if (idx !== -1) { cart[idx].quantity += 1; cart[idx].stock = actualStock; onShowToast(`"${product.productName} ${variantLabel}" updated in cart!`, 'info'); }
    else { cart.push({ cartId, id: product.id, variantId: normalizeVariantId(selectedVariant.id), name: product.productName, variantName: selectedVariant.variantName || '', unit: selectedVariant.unit || '', weight: variantLabel, img: imgSrc, price: salesPrice, quantity: 1, stock: actualStock }); onShowToast(`"${product.productName} ${variantLabel}" added to cart!`, 'success'); }
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('storage'));
    try {
        const res = await fetch(`${API_BASE_URL}/cart/add`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, productId: product.id, variantId: normalizeVariantId(selectedVariant.id) !== 'default' ? selectedVariant.id : null, variantName: selectedVariant.variantName || null, name: product.productName, img: imgSrc, price: salesPrice, quantity: 1, weight: variantLabel }) });
        if (res.ok) window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) { console.error('Cart API network error:', err.message); }
};


 const toggleWishlist = async (e) => {
    e.stopPropagation();
 
    // ── Guest check ──────────────────────────────────────────
    const customerId = getCustomerId();
    if (!customerId) {
        onShowToast('Please login to save items to your wishlist!', 'warn');
        return;
    }
 
    const wl = JSON.parse(localStorage.getItem('wishlist')) || [];
    const inList = wl.find(i => i.id === product.id);
    let updated;
    if (inList) {
        updated = wl.filter(i => i.id !== product.id);
        setIsWishlisted(false);
        onShowToast('Removed from favorites.', 'info');
        try { const res = await fetch(`${API_BASE_URL}/wishlist/${customerId}`); const data = await res.json(); const item = data.find(i => i.productId === product.id); if (item) { await fetch(`${API_BASE_URL}/wishlist/${item.id}`, { method: 'DELETE' }); window.dispatchEvent(new Event('cartUpdated')); } } catch (err) { console.error(err); }
    } else {
        updated = [...wl, { id: product.id, name: product.productName, img: imgSrc, price: salesPrice }];
        setIsWishlisted(true);
        onShowToast('Added to favorites.', 'success');
        try { await fetch(`${API_BASE_URL}/wishlist/add`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, productId: product.id, name: product.productName, img: imgSrc, price: salesPrice, weight: variantLabel, stock: true }) }); window.dispatchEvent(new Event('cartUpdated')); } catch (err) { console.error(err); }
    }
    localStorage.setItem('wishlist', JSON.stringify(updated));
    window.dispatchEvent(new Event('storage'));
};


    const addDisabled  = isOutOfStock || isFullyCarted;
    const cartBtnLabel = addDisabled ? 'Out of Stock' : '+ Add to Cart';

    return (
        <div className="pc-card" style={{ opacity: isOutOfStock ? 0.85 : 1 }}>
            <div className="pc-img-wrap" onClick={handleProductClick}>
                <img src={imgSrc} alt={product.productName} className="pc-img" />
                {(isOutOfStock || isFullyCarted) ? <span className="pc-oos-badge">Out of Stock</span> : discountPct > 0 && <span className="pc-discount-badge">{discountPct}% OFF</span>}
                {isLowStock && !isFullyCarted && <span className="pc-low-stock-badge">Only {actualStock} left!</span>}
                <div className="pc-action-bar">
                    <button className="pc-icon-btn" onClick={toggleWishlist}>{isWishlisted ? '❤️' : '🤍'}</button>
                    <button className="pc-cart-btn" onClick={handleAdd} disabled={addDisabled}>{cartBtnLabel}</button>
                    <button className="pc-icon-btn" onClick={handleQuickView}>🔍</button>
                </div>
            </div>
            <div className="pc-body">
                <span className="pc-category-label">{product.Category?.name}</span>
                <h3 className="pc-name" onClick={handleProductClick}>{product.productName}</h3>
                {product.Brand?.brandName && <span className="pc-brand">{product.Brand.brandName}</span>}
                {product.variants?.length > 0 && (
                    <div className="pc-variants">
                        {product.variants.map(v => {
                            const vStock = parseInt(v.stock ?? 999), vOos = vStock === 0;
                            const vCarted = getCartedQty(product.id, v.id), vMaxed = !vOos && vCarted >= vStock;
                            return (
                                <button key={v.id} className={`pc-variant-btn${selectedVariant.id === v.id ? ' active' : ''}${(vOos || vMaxed) ? ' oos' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); if (!vOos && !vMaxed) setSelectedVariant(v); }}
                                    title={vOos ? 'Out of stock' : vMaxed ? `All ${vStock} in cart` : `${vStock - vCarted} remaining`}>
                                    {getVariantLabel(v)}{(vOos || vMaxed) && <span style={{ fontSize: 8, marginLeft: 2 }}>✕</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className="pc-stars">★★★★★</div>
                <div className="pc-price-row">
                    <span className="pc-price">₹{salesPrice}</span>
                    {mrp > salesPrice && <span className="pc-old-price">₹{mrp}</span>}
                </div>
                {mrp > salesPrice && <span className="pc-save-tag">SAVE ₹{(mrp - salesPrice).toFixed(0)}</span>}
            </div>
            <div className="pc-mobile-footer">
                <button className="pc-mobile-wish-btn" onClick={toggleWishlist}>{isWishlisted ? '❤️' : '🤍'}</button>
                <button className="pc-mobile-cart-btn" onClick={handleAdd} disabled={addDisabled}
                    style={{ background: addDisabled ? '#9CA3AF' : '#0C3D7A', cursor: addDisabled ? 'not-allowed' : 'pointer', opacity: addDisabled ? 0.7 : 1 }}>
                    {addDisabled ? '🚫 Out of Stock' : '🛒 Add to Cart'}
                </button>
                <button className="pc-mobile-qv-btn" onClick={handleQuickView}>🔍</button>
            </div>
        </div>
    );
};

// ─── SidebarContent ───────────────────────────
const SidebarContent = ({
    categories, visibleSubCats, brands, loading,
    activeCategory, setActiveCategory,
    activeSubCategory, setActiveSubCategory,
    activeBrand, setActiveBrand,
    priceRange, setPriceRange, maxPrice,
    hasActiveFilter, clearAll, sectionTitle,
    onClose,
}) => {
    const filterContent = (
        <>
            <p style={{ ...sectionTitle, marginTop: 0 }}>Price Range</p>
            <PriceFilter priceRange={priceRange} setPriceRange={setPriceRange} maxPrice={maxPrice} />

            <p style={{ ...sectionTitle, marginTop: '20px' }}>Category</p>
            <button className={`sb-filter-btn${activeCategory === 'All' ? ' active' : ''}`}
                onClick={() => { setActiveCategory('All'); setActiveSubCategory('All'); if (onClose) onClose(); }}>
                All Products
            </button>
            {categories.map(cat => (
                <button key={cat.id} className={`sb-filter-btn${activeCategory === cat.name ? ' active' : ''}`}
                    onClick={() => { setActiveCategory(cat.name); setActiveSubCategory('All'); if (onClose) onClose(); }}>
                    {cat.name}
                </button>
            ))}

            <p style={sectionTitle}>Sub Category</p>
            <button className={`sb-img-btn${activeSubCategory === 'All' ? ' active' : ''}`}
                onClick={() => { setActiveSubCategory('All'); if (onClose) onClose(); }}>
                <span className="sb-img-placeholder">A</span>
                All
            </button>
            {visibleSubCats.map(sc => (
                <button key={sc.id} className={`sb-img-btn${activeSubCategory === sc.name ? ' active' : ''}`}
                    onClick={() => { setActiveSubCategory(sc.name); if (onClose) onClose(); }}>
                    <SidebarThumb src={sc.image ? getImageUrl(sc.image) : null} alt={sc.name} />
                    {sc.name}
                </button>
            ))}
            {visibleSubCats.length === 0 && (
                <p style={{ fontSize: '12px', color: '#ccc', padding: '4px 10px', fontFamily: 'DM Sans, sans-serif' }}>
                    No sub categories
                </p>
            )}

            <p style={sectionTitle}>Brand</p>
            <button className={`sb-img-btn${activeBrand === 'All' ? ' active' : ''}`}
                onClick={() => { setActiveBrand('All'); if (onClose) onClose(); }}>
                <span className="sb-img-placeholder">A</span>
                All
            </button>
            {brands.map(b => (
                <button key={b.id} className={`sb-img-btn${activeBrand === b.brandName ? ' active' : ''}`}
                    onClick={() => { setActiveBrand(b.brandName); if (onClose) onClose(); }}>
                    <SidebarThumb src={b.logo ? getImageUrl(b.logo) : null} alt={b.brandName} />
                    {b.brandName}
                </button>
            ))}
            {brands.length === 0 && !loading && (
                <p style={{ fontSize: '12px', color: '#ccc', padding: '4px 10px', fontFamily: 'DM Sans, sans-serif' }}>
                    No brands available
                </p>
            )}
        </>
    );

    if (onClose) {
        return (
            <>
                <div className="sb-modal-header">
                    <span className="sb-modal-title">Filters</span>
                    <button className="sb-drawer-close" onClick={onClose}>✕</button>
                </div>
                <div className="sb-modal-body">{filterContent}</div>
                <div className="sb-modal-footer">
                    {hasActiveFilter && <button className="sb-clear-btn" onClick={() => { clearAll(); onClose(); }}>Clear All</button>}
                    <button className="sb-apply-btn" onClick={onClose}>Show Results</button>
                </div>
            </>
        );
    }

    return (
        <>
            {filterContent}
            {hasActiveFilter && (
                <button onClick={clearAll} style={{ display: 'block', width: '100%', marginTop: '16px', padding: '8px', background: '#FEF0E5', border: '1px solid #F5CBA7', borderRadius: '8px', color: colors.orange, fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.04em' }}>
                    ✕ Clear All Filters
                </button>
            )}
        </>
    );
};

// ─── ShopSection Main ─────────────────────────
const ShopSection = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const categoryParam = query.get('category') ? decodeURIComponent(query.get('category')) : null;

    const [toast, setToast]                         = useState({ show: false, message: '', type: '' });
    const [products, setProducts]                   = useState([]);
    const [categories, setCategories]               = useState([]);
    const [subCategories, setSubCategories]         = useState([]);
    const [brands, setBrands]                       = useState([]);
    const [loading, setLoading]                     = useState(true);
    const [activeCategory, setActiveCategory]       = useState(categoryParam || 'All');
    const [activeSubCategory, setActiveSubCategory] = useState('All');
    const [activeBrand, setActiveBrand]             = useState('All');
    const [activeUnit, setActiveUnit]               = useState('All');
    const [activeSize, setActiveSize]               = useState('All');
    const [currentPage, setCurrentPage]             = useState(1);
    const [drawerOpen, setDrawerOpen]               = useState(false);
    const [maxPrice, setMaxPrice]                   = useState(10000);
    const [priceRange, setPriceRange]               = useState([0, 10000]);

    useEffect(() => { setActiveCategory(categoryParam || 'All'); setActiveSubCategory('All'); }, [categoryParam]);
    useEffect(() => { setCurrentPage(1); }, [activeCategory, activeSubCategory, activeBrand, activeUnit, activeSize, priceRange]);

    useEffect(() => {
        if (drawerOpen) { document.body.style.overflow = 'hidden'; window.dispatchEvent(new CustomEvent('shopFilterDrawer', { detail: { open: true } })); }
        else { document.body.style.overflow = ''; window.dispatchEvent(new CustomEvent('shopFilterDrawer', { detail: { open: false } })); }
        return () => { document.body.style.overflow = ''; };
    }, [drawerOpen]);

    useEffect(() => {
        const onResize = () => { if (window.innerWidth > 1024) setDrawerOpen(false); };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const [cRes, scRes, bRes] = await Promise.all([getAllCategories(), getAllSubCategories(), getAllBrands()]);
                setCategories(cRes.data || cRes || []);
                setSubCategories(scRes.data || scRes || []);
                setBrands((bRes.data || bRes || []).filter(b => !b.status || b.status.toLowerCase() === 'active'));
            } catch (err) { console.error('Meta fetch error:', err); }
        };
        fetchMeta();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const pRes = await getAllProducts();
                const rawProducts = pRes.data || pRes || [];
                const pWithV = await Promise.all(rawProducts.map(async (p) => { const vRes = await getVariantsByProduct(p.id); return { ...p, variants: vRes.data || [] }; }));
                setProducts(pWithV);
                const allPrices = pWithV.flatMap(p => p.variants?.length > 0 ? p.variants.map(v => parseFloat(v.salesPrice) || 0) : [parseFloat(p.salesPrice) || 0]);
                const niceMax = Math.ceil(Math.max(...allPrices, 1000) / 500) * 500;
                setMaxPrice(niceMax);
                setPriceRange([0, niceMax]);
            } catch (err) { console.error('Products fetch error:', err); }
            finally { setLoading(false); }
        };
        fetchProducts();
    }, []);

    const filtered = useMemo(() => {
        return products.filter(p => {
            const catMatch   = activeCategory    === 'All' || p.Category?.name === activeCategory;
            const subMatch   = activeSubCategory === 'All' || p.SubCategory?.name === activeSubCategory || p.subCategoryId === subCategories.find(sc => sc.name === activeSubCategory)?.id;
            const brandMatch = activeBrand       === 'All' || p.Brand?.brandName === activeBrand;
            const unitMatch  = activeUnit        === 'All' || (p.variants || []).some(v => { const u = (v.unit || '').trim().toLowerCase(), n = (v.variantName || '').trim().toLowerCase(); return u === activeUnit.toLowerCase() || n === activeUnit.toLowerCase(); });
            const sizeMatch  = activeSize        === 'All' || (p.variants || []).some(v => { const u = (v.unit || '').trim().toUpperCase(), n = (v.variantName || '').trim().toUpperCase(); return u === activeSize || n === activeSize; });
            const effectivePrice = p.variants?.length > 0 ? parseFloat(p.variants[0].salesPrice) || 0 : parseFloat(p.salesPrice) || 0;
            const priceMatch = effectivePrice >= priceRange[0] && effectivePrice <= priceRange[1];
            return catMatch && subMatch && brandMatch && unitMatch && sizeMatch && priceMatch;
        });
    }, [products, activeCategory, activeSubCategory, activeBrand, activeUnit, activeSize, subCategories, priceRange]);

    const totalPages        = Math.ceil(filtered.length / PRODUCTS_PER_PAGE);
    const paginatedProducts = filtered.slice((currentPage - 1) * PRODUCTS_PER_PAGE, currentPage * PRODUCTS_PER_PAGE);
    const visibleSubCats    = activeCategory === 'All' ? subCategories : subCategories.filter(sc => sc.Category?.name === activeCategory || sc.categoryId === categories.find(c => c.name === activeCategory)?.id);
    const isPriceFiltered   = priceRange[0] !== 0 || priceRange[1] !== maxPrice;
    const hasActiveFilter   = activeCategory !== 'All' || activeSubCategory !== 'All' || activeBrand !== 'All' || activeUnit !== 'All' || activeSize !== 'All' || isPriceFiltered;

    const clearAll = () => {
        setActiveCategory('All'); setActiveSubCategory('All');
        setActiveBrand('All'); setActiveUnit('All'); setActiveSize('All');
        setPriceRange([0, maxPrice]); setCurrentPage(1);
    };

    const sectionTitle = {
        fontSize: '11px', fontWeight: '700', color: colors.brown,
        borderBottom: `2px solid ${colors.orange}`, paddingBottom: '7px',
        marginBottom: '8px', marginTop: '18px', textTransform: 'uppercase',
        letterSpacing: '0.08em', fontFamily: 'DM Sans, sans-serif',
    };

    const sidebarProps = {
        categories, visibleSubCats, brands, loading,
        activeCategory, setActiveCategory,
        activeSubCategory, setActiveSubCategory,
        activeBrand, setActiveBrand,
        priceRange, setPriceRange, maxPrice,
        hasActiveFilter, clearAll, sectionTitle,
    };

    const renderHeading = () => {
        const sep = <span style={{ fontSize: '18px', color: '#ddd', margin: '0 5px' }}>›</span>;
        return (
            <h4 style={{ fontSize: '20px', color: colors.textPrimary, margin: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px', fontFamily: 'Playfair Display, serif', fontWeight: 600 }}>
                <span>{activeCategory === 'All' ? 'All Products' : activeCategory}</span>
                {activeSubCategory !== 'All' && <>{sep}<span style={{ fontSize: '16px', color: colors.orange, fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>{activeSubCategory}</span></>}
                {activeBrand !== 'All' && <>{sep}<span style={{ fontSize: '16px', color: colors.green, fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>{activeBrand}</span></>}
            </h4>
        );
    };

    return (
        <section style={{ minHeight: '100vh', fontFamily: 'DM Sans, sans-serif' }}>
            <style>{GLOBAL_STYLES}</style>

            {toast.show && (
                <div className={`rc-toast rc-toast-${toast.type}`}>
                    <div className="rc-toast-body">
                        <strong style={{ fontSize: '13px' }}>
                            {toast.type === 'success' ? '🛒 Added to cart!' : toast.type === 'warn' ? '⚠️ Cannot add!' : '🔄 Updated!'}
                        </strong>
                        <div style={{ fontSize: '12px', opacity: 0.85 }}>{toast.message}</div>
                    </div>
                    <div className="rc-toast-progress" />
                </div>
            )}

            <div className={`sb-overlay${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} />
            <div className={`sb-drawer${drawerOpen ? ' open' : ''}`}>
                <SidebarContent {...sidebarProps} onClose={() => setDrawerOpen(false)} />
            </div>

            <CategoryStrip
                categories={categories}
                activeCategory={activeCategory}
                onSelect={(name) => { setActiveCategory(name); setActiveSubCategory('All'); setActiveBrand('All'); }}
            />

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem 3rem' }}>
                <div className="shop-layout">
                    <aside className="shop-sidebar-static">
                        <SidebarContent {...sidebarProps} onClose={null} />
                    </aside>
                    <main className="shop-main">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <button className="filter-toggle-btn" onClick={() => setDrawerOpen(true)}>
                                    ☰ Filters
                                    {hasActiveFilter && <span style={{ background: colors.orange, color: '#fff', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700 }}>●</span>}
                                </button>
                                {renderHeading()}
                                <span style={{ fontSize: '13px', color: '#aaa', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' }}>({filtered.length} products)</span>
                            </div>
                            <PriceDropdown priceRange={priceRange} setPriceRange={setPriceRange} maxPrice={maxPrice} />
                        </div>

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '80px 0', color: '#bbb', fontSize: '14px', fontFamily: 'DM Sans, sans-serif' }}>Loading products…</div>
                        ) : filtered.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '80px 0', color: '#bbb', fontSize: '14px', fontFamily: 'DM Sans, sans-serif' }}>
                                No products found.{' '}
                                <button onClick={clearAll} style={{ color: colors.orange, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>Clear filters</button>
                            </div>
                        ) : (
                            <>
                                <div className="shop-products-grid">
                                    {paginatedProducts.map(product => (
                                        <ProductCard key={product.id} product={product} onShowToast={showToast} />
                                    ))}
                                </div>
                                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                            </>
                        )}
                    </main>
                </div>
            </div>
        </section>
    );
};

export default ShopSection;